import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { adminGetUsers } from "../../library/allauth";
import {
  Users, UserCheck, Clock, UserX,
  LogIn, UserPlus, Mail, ArrowRight
} from "lucide-react";

function timeAgo(dateStr) {
  if (!dateStr) return "Never";
  const diff = Math.floor((Date.now() - new Date(dateStr)) / 1000);
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)} min ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} hours ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)} days ago`;
  return new Date(dateStr).toLocaleDateString();
}

export default function Overview({ onNavigate }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    adminGetUsers()
      .then((res) => { if (res.status === 200) setUsers(res.data); })
      .finally(() => setLoading(false));
  }, []);

  const counts = {
    total:    users.length,
    active:   users.filter((u) => u.status === "active").length,
    pending:  users.filter((u) => u.status === "pending").length,
    disabled: users.filter((u) => u.status === "disabled").length,
  };

  // Build recent activity from user data
  const activity = users
    .flatMap((u) => {
      const events = [];
      if (u.last_login) {
        events.push({
          type: u.status === "pending" ? "invite_sent" : "logged_in",
          user: u.email || u.username,
          time: u.last_login,
          label: u.status === "pending" ? "invite sent" : "logged in",
        });
      }
      events.push({
        type: "registered",
        user: u.email || u.username,
        time: u.date_joined,
        label: "registered",
      });
      return events;
    })
    .sort((a, b) => new Date(b.time) - new Date(a.time))
    .slice(0, 8);

  const ACTIVITY_ICONS = {
    logged_in:    { icon: LogIn,    color: "bg-green-100 text-green-600" },
    invite_sent:  { icon: Mail,     color: "bg-yellow-100 text-yellow-600" },
    registered:   { icon: UserPlus, color: "bg-blue-100 text-blue-600" },
  };

  const STAT_CARDS = [
    {
      label: "Total Users",
      value: counts.total,
      icon: Users,
      color: "bg-blue-50 text-blue-600",
      border: "border-blue-100",
      filter: "all",
    },
    {
      label: "Active Users",
      value: counts.active,
      icon: UserCheck,
      color: "bg-green-50 text-green-600",
      border: "border-green-100",
      filter: "active",
    },
    {
      label: "Pending Invites",
      value: counts.pending,
      icon: Clock,
      color: "bg-yellow-50 text-yellow-600",
      border: "border-yellow-100",
      filter: "pending",
    },
    {
      label: "Disabled Users",
      value: counts.disabled,
      icon: UserX,
      color: "bg-red-50 text-red-600",
      border: "border-red-100",
      filter: "disabled",
    },
  ];

  return (
    <div className="max-w-6xl mx-auto">

      {/* Page Header */}
      <div className="mb-8">
        <h1 className="text-xl font-semibold text-gray-900">Overview</h1>
        <p className="text-sm text-gray-400 mt-0.5">
          Portal user summary and recent activity
        </p>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-4 gap-5 mb-8">
        {loading
          ? Array(4).fill(0).map((_, i) => (
              <div key={i} className="bg-white rounded-xl border border-gray-200 p-5 animate-pulse">
                <div className="h-4 bg-gray-100 rounded mb-3 w-2/3" />
                <div className="h-8 bg-gray-100 rounded w-1/3" />
              </div>
            ))
          : STAT_CARDS.map((card) => {
              const Icon = card.icon;
              return (
                <button
                  key={card.label}
                  onClick={() => onNavigate("users", card.filter)}
                  className={`bg-white rounded-xl border ${card.border} p-5 text-left hover:shadow-md transition-all group`}
                >
                  <div className="flex items-center justify-between mb-4">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${card.color}`}>
                      <Icon size={18} />
                    </div>
                    <ArrowRight
                      size={16}
                      className="text-gray-300 group-hover:text-gray-500 transition-colors"
                    />
                  </div>
                  <p className="text-3xl font-bold text-gray-900 mb-1">{card.value}</p>
                  <p className="text-sm text-gray-400">{card.label}</p>
                </button>
              );
            })}
      </div>

      {/* Recent Activity + Quick Actions */}
      <div className="grid grid-cols-3 gap-6">

        {/* Recent Activity — takes 2 cols */}
        <div className="col-span-2 bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-5">
            <h2 className="text-sm font-semibold text-gray-700">Recent Activity</h2>
            <button
              onClick={() => onNavigate("users")}
              className="text-xs text-emerald-600 hover:text-emerald-800 font-medium flex items-center gap-1 transition-colors"
            >
              View all users
              <ArrowRight size={13} />
            </button>
          </div>

          {loading ? (
            <div className="space-y-4">
              {Array(5).fill(0).map((_, i) => (
                <div key={i} className="flex items-center gap-3 animate-pulse">
                  <div className="w-8 h-8 bg-gray-100 rounded-full flex-shrink-0" />
                  <div className="flex-1">
                    <div className="h-3 bg-gray-100 rounded w-3/4 mb-1.5" />
                    <div className="h-2.5 bg-gray-100 rounded w-1/4" />
                  </div>
                </div>
              ))}
            </div>
          ) : activity.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-8">No activity yet</p>
          ) : (
            <div className="space-y-1">
              {activity.map((item, i) => {
                const { icon: Icon, color } = ACTIVITY_ICONS[item.type] || ACTIVITY_ICONS.registered;
                return (
                  <div
                    key={i}
                    className="flex items-center gap-3 p-2.5 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${color}`}>
                      <Icon size={14} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-800 truncate font-medium">
                        {item.user}
                      </p>
                      <p className="text-xs text-gray-400 capitalize">{item.label}</p>
                    </div>
                    <p className="text-xs text-gray-400 flex-shrink-0">{timeAgo(item.time)}</p>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Quick Actions — 1 col */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h2 className="text-sm font-semibold text-gray-700 mb-5">Quick Actions</h2>
          <div className="space-y-3">
            <button
              onClick={() => onNavigate("register")}
              className="w-full flex items-center gap-3 p-3.5 rounded-xl border border-emerald-200 bg-emerald-50 hover:bg-emerald-100 transition-colors group text-left"
            >
              <div className="w-9 h-9 bg-emerald-700 rounded-lg flex items-center justify-center flex-shrink-0">
                <UserPlus size={16} className="text-white" />
              </div>
              <div>
                <p className="text-sm font-medium text-emerald-900">Register User</p>
                <p className="text-xs text-emerald-600">Create & send invite</p>
              </div>
            </button>

            <button
              onClick={() => onNavigate("users", "pending")}
              className="w-full flex items-center gap-3 p-3.5 rounded-xl border border-yellow-200 bg-yellow-50 hover:bg-yellow-100 transition-colors group text-left"
            >
              <div className="w-9 h-9 bg-yellow-400 rounded-lg flex items-center justify-center flex-shrink-0">
                <Clock size={16} className="text-white" />
              </div>
              <div>
                <p className="text-sm font-medium text-yellow-900">Pending Invites</p>
                <p className="text-xs text-yellow-600">
                  {counts.pending} invite{counts.pending !== 1 ? "s" : ""} waiting
                </p>
              </div>
            </button>

            <button
              onClick={() => onNavigate("users", "active")}
              className="w-full flex items-center gap-3 p-3.5 rounded-xl border border-gray-200 bg-gray-50 hover:bg-gray-100 transition-colors group text-left"
            >
              <div className="w-9 h-9 bg-gray-600 rounded-lg flex items-center justify-center flex-shrink-0">
                <Users size={16} className="text-white" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-800">Manage Users</p>
                <p className="text-xs text-gray-500">{counts.active} active users</p>
              </div>
            </button>
          </div>
        </div>

      </div>
    </div>
  );
}